import type { NostrEvent } from '@nostrify/nostrify';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  extractArticleTitle,
  extractArticleSummary,
  extractArticleImage,
  extractArticleDTag,
  extractArticlePublishedAt,
  extractArticleTags,
} from '@/hooks/useArticles';
import { Link } from 'react-router-dom';
import { Calendar } from 'lucide-react';

interface ArticleCardProps {
  event: NostrEvent;
}

export function ArticleCard({ event }: ArticleCardProps) {
  const title = extractArticleTitle(event);
  const summary = extractArticleSummary(event);
  const image = extractArticleImage(event);
  const dTag = extractArticleDTag(event);
  const publishedAt = extractArticlePublishedAt(event);
  const tags = extractArticleTags(event);

  const timestamp = publishedAt || event.created_at;
  const dateStr = new Date(timestamp * 1000).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (!dTag) return null;

  return (
    <Link to={`/article/${dTag}`}>
      <Card className="hover:shadow-xl transition-all duration-300 group h-full">
        {image && (
          <div className="overflow-hidden">
            <img
              src={image}
              alt={title || 'Article'}
              className="w-full h-48 object-cover transform group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          </div>
        )}
        <CardHeader className="pb-3">
          <CardTitle className="text-xl group-hover:text-primary transition-colors">
            {title || 'Untitled Article'}
          </CardTitle>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>{dateStr}</span>
          </div>
        </CardHeader>
        {summary && (
          <CardContent className="pb-3">
            <p className="text-sm text-muted-foreground line-clamp-3">{summary}</p>
          </CardContent>
        )}
        {tags.length > 0 && (
          <CardFooter className="flex-wrap gap-2">
            {tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
            {tags.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{tags.length - 3}
              </Badge>
            )}
          </CardFooter>
        )}
      </Card>
    </Link>
  );
}
